/**
 * AES-256-GCM application-level decryption — mirrors Java AesEncryptionService.
 *
 * Algorithm  : AES-256-GCM
 * KDF        : PBKDF2-HMAC-SHA256, 310 000 iterations
 * IV         : 12 bytes prepended to the cipher payload by Java
 * Auth tag   : 128-bit GCM tag appended after ciphertext by Java
 * Wire format: Base64URL (no padding):  IV[12] || Ciphertext || AuthTag[16]
 *
 * Performance notes:
 *   - PBKDF2 key derivation runs ONCE per page-load and is cached.
 *   - All string fields inside an object are decrypted in parallel via Promise.all.
 *   - Short strings that cannot possibly be valid cipher payloads are fast-pathed without
 *     touching the crypto subsystem.
 *
 * Environment variables (must be set before building):
 *   REACT_APP_ENCRYPTION_SECRET_KEY  — 32+ char AES secret (mirrors app.encryption.secret-key)
 *   REACT_APP_ENCRYPTION_SALT        — 16+ char PBKDF2 salt  (mirrors app.encryption.salt)
 */

import forge from 'node-forge';

// ─── Configuration ───────────────────────────────────────────────────────────

// Trim to guard against accidental env-var whitespace
const SECRET = (process.env.REACT_APP_ENCRYPTION_SECRET_KEY ?? '').trim();
const SALT   = (process.env.REACT_APP_ENCRYPTION_SALT ?? '').trim();

const KDF_ITERATIONS = 310_000;
const GCM_IV_BYTES   = 12;
const GCM_TAG_BYTES  = 16;

/**
 * Minimum decoded payload bytes:  IV (12) + 1 byte plaintext + AuthTag (16) = 29.
 * Minimum Base64URL chars (no padding) for 29 bytes:
 *   29 bytes → groups of 3 → ceil(29/3)=10 groups → 10*4=40 chars with padding → 39 without.
 */
const MIN_PAYLOAD_BYTES = GCM_IV_BYTES + GCM_TAG_BYTES + 1; // 29
const MIN_B64URL_CHARS  = 39;

const IS_DEV = process.env.NODE_ENV !== 'production';
const INSECURE_FALLBACK_ORIGIN = 'http://222.165.138.144:85';
const CAN_USE_INSECURE_FALLBACK =
  typeof window !== 'undefined' &&
  window.location.origin === INSECURE_FALLBACK_ORIGIN;

let _prodInsecureWarningShown = false;

// ─── Key derivation (cached) ──────────────────────────────────────────────────

/**
 * Holds the in-flight or resolved key Promise.
 * Explicitly reset to null on rejection so the next call retries,
 * preventing a single transient failure from poisoning all future calls.
 */
let _webCryptoKeyPromise: Promise<CryptoKey> | null = null;
let _forgeKeyPromise: Promise<string> | null = null;

function getWebCryptoKey(): Promise<CryptoKey> {
  if (_webCryptoKeyPromise) return _webCryptoKeyPromise;

  _webCryptoKeyPromise = deriveWebCryptoKey().catch((err: unknown) => {
    _webCryptoKeyPromise = null; // do NOT cache failures — allow retry
    throw err;
  });

  return _webCryptoKeyPromise;
}

function getForgeKey(): Promise<string> {
  if (_forgeKeyPromise) return _forgeKeyPromise;

  _forgeKeyPromise = Promise.resolve()
    .then(() => deriveForgeKey())
    .catch((err: unknown) => {
      _forgeKeyPromise = null; // do NOT cache failures — allow retry
      throw err;
    });

  return _forgeKeyPromise;
}

async function deriveWebCryptoKey(): Promise<CryptoKey> {
  const subtle = window.crypto?.subtle;
  if (!subtle) {
    throw new Error(
      '[CryptoUtils] SubtleCrypto is unavailable. ' +
      'The application must be served over HTTPS or localhost.'
    );
  }
  if (!SECRET || !SALT) {
    throw new Error(
      '[CryptoUtils] REACT_APP_ENCRYPTION_SECRET_KEY and REACT_APP_ENCRYPTION_SALT ' +
      'must be configured before building. Restart the dev server after editing .env.'
    );
  }

  const enc         = new TextEncoder();
  const keyMaterial = await subtle.importKey(
    'raw',
    enc.encode(SECRET),
    { name: 'PBKDF2' },
    false,
    ['deriveKey']
  );

  return subtle.deriveKey(
    {
      name      : 'PBKDF2',
      salt      : enc.encode(SALT),
      iterations: KDF_ITERATIONS,
      hash      : 'SHA-256',
    },
    keyMaterial,
    { name: 'AES-GCM', length: 256 },
    false,
    ['decrypt']
  );
}

function deriveForgeKey(): string {
  if (!SECRET || !SALT) {
    throw new Error(
      '[CryptoUtils] REACT_APP_ENCRYPTION_SECRET_KEY and REACT_APP_ENCRYPTION_SALT ' +
      'must be configured before building. Restart the dev server after editing .env.'
    );
  }

  return forge.pkcs5.pbkdf2(
    SECRET,
    SALT,
    KDF_ITERATIONS,
    32,
    forge.md.sha256.create()
  );
}

function bytesToBinary(bytes: Uint8Array): string {
  let binary = '';
  for (let i = 0; i < bytes.length; i += 1) {
    binary += String.fromCharCode(bytes[i]);
  }
  return binary;
}

async function decryptWithWebCrypto(
  payload: Uint8Array,
  key: CryptoKey
): Promise<string> {
  const iv             = payload.slice(0, GCM_IV_BYTES);
  const encryptedBytes = payload.slice(GCM_IV_BYTES); // ciphertext + auth tag

  const plainBuffer = await window.crypto.subtle.decrypt(
    { name: 'AES-GCM', iv, tagLength: 128 },
    key,
    encryptedBytes
  );

  return new TextDecoder().decode(plainBuffer);
}

function decryptWithForge(payload: Uint8Array, key: string): string | null {
  const iv         = payload.slice(0, GCM_IV_BYTES);
  const cipherAndTag = payload.slice(GCM_IV_BYTES);

  if (cipherAndTag.length <= GCM_TAG_BYTES) return null;

  const ciphertext = cipherAndTag.slice(0, cipherAndTag.length - GCM_TAG_BYTES);
  const tag        = cipherAndTag.slice(cipherAndTag.length - GCM_TAG_BYTES);

  const decipher = forge.cipher.createDecipher('AES-GCM', key);
  decipher.start({
    iv       : bytesToBinary(iv),
    tagLength: 128,
    tag      : forge.util.createBuffer(bytesToBinary(tag)),
  });
  decipher.update(forge.util.createBuffer(bytesToBinary(ciphertext)));

  const ok = decipher.finish();
  if (!ok) return null;

  return forge.util.decodeUtf8(decipher.output.getBytes());
}

// ─── String decryption ────────────────────────────────────────────────────────

/**
 * Attempts AES-256-GCM decryption of one Base64URL-encoded ciphertext string.
 *
 * Returns the original `value` unchanged when any of the following are true:
 *   • The string is shorter than the minimum valid cipher payload (fast-path).
 *   • The string is not valid Base64 (plain-text field, e.g. "Male", UUID).
 *   • GCM authentication fails (string was not encrypted with the configured key).
 *   • The crypto subsystem is unavailable.
 *
 * In development mode a warning is logged for every failure mode so that
 * configuration problems (wrong key, wrong format, etc.) surface immediately.
 */
async function tryDecryptString(value: string): Promise<string> {
  // Fast-path: plain-text values (UUIDs, short labels, etc.) skip the crypto subsystem entirely.
  if (!value || value.length < MIN_B64URL_CHARS) return value;

  // Base64URL → standard Base64 with padding.
  // Handles both URL-safe (- / _) and standard (+ / /) Base64 from the backend.
  const b64    = value.replace(/-/g, '+').replace(/_/g, '/');
  const padded = b64 + '='.repeat((4 - (b64.length % 4)) % 4);

  let binary: string;
  try {
    binary = atob(padded);
  } catch {
    // Not valid Base64 → definitely a plain-text field, skip silently.
    return value;
  }

  const payload = Uint8Array.from(binary, (c) => c.charCodeAt(0));

  if (payload.length < MIN_PAYLOAD_BYTES) return value; // decoded too short → not a cipher

  let webCryptoKey: CryptoKey | null = null;
  try {
    webCryptoKey = await getWebCryptoKey();
  } catch (err: unknown) {
    if (IS_DEV) {
      console.warn(
        '[CryptoUtils] WebCrypto key unavailable. ' +
        `Secure-context required unless fallback origin is ${INSECURE_FALLBACK_ORIGIN}.`,
        err
      );
    } else if (!window.isSecureContext && !_prodInsecureWarningShown) {
      _prodInsecureWarningShown = true;
      console.error(
        '[CryptoUtils] Insecure origin detected. ' +
        `Only localhost/HTTPS and ${INSECURE_FALLBACK_ORIGIN} are supported for decryption.`
      );
    }
  }

  if (webCryptoKey) {
    try {
      return await decryptWithWebCrypto(payload, webCryptoKey);
    } catch {
      if (IS_DEV) {
        console.warn(
          `[CryptoUtils] AES-GCM auth-tag verification failed for a ${value.length}-char field ` +
          `(first 20 chars: "${value.slice(0, 20)}..."). ` +
          'Either this field is plain text that looks like Base64, or the derived key does not ' +
          'match the backend. Verify REACT_APP_ENCRYPTION_SECRET_KEY and ' +
          'REACT_APP_ENCRYPTION_SALT against the Java backend config.'
        );
      }
      return value;
    }
  }

  if (!CAN_USE_INSECURE_FALLBACK) return value;

  try {
    const forgeKey = await getForgeKey();
    const plain    = decryptWithForge(payload, forgeKey);

    if (plain !== null) return plain;
  } catch {
    if (IS_DEV) {
      console.warn(
        '[CryptoUtils] Insecure-origin fallback decrypt failed. ' +
        'Verify encryption key/salt and Java AES-GCM payload format.'
      );
    }
  }

  return value;
}

// ─── Public API ───────────────────────────────────────────────────────────────

/** Narrowing alias: a plain JSON object after routing through `unknown`. */
type PlainObject = Record<string, unknown>;

/**
 * Recursively walks any JSON-compatible payload and decrypts every string leaf
 * in parallel.  Non-string primitives (numbers, booleans, null, dates) are
 * returned unchanged.  The input shape is fully preserved.
 *
 * Usage:
 * ```ts
 * const plain = await decryptResponseData(response.data);
 * ```
 */
export async function decryptResponseData<T>(data: T): Promise<T> {
  if (data === null || data === undefined) return data;

  if (typeof data === 'string') {
    return tryDecryptString(data) as unknown as T;
  }

  if (typeof data !== 'object') {
    return data; // number, boolean, bigint, symbol — pass through
  }

  if (Array.isArray(data)) {
    const decrypted = await Promise.all(
      (data as unknown[]).map((item) => decryptResponseData(item))
    );
    return decrypted as unknown as T;
  }

  // Plain object: decrypt all values in parallel, preserve all keys.
  const plainObj = data as unknown as PlainObject;
  const entries  = await Promise.all(
    Object.entries(plainObj).map(async ([k, v]): Promise<[string, unknown]> => [
      k,
      await decryptResponseData(v),
    ])
  );

  return Object.fromEntries(entries) as unknown as T;
}

